import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAuthStore } from '@/lib/auth/store';
import { cn } from '@/lib/utils';

import { ApiErrorBanner } from './ApiErrorBanner';
import { StatusBadge } from './StatusBadge';
import {
  useApproveFlowModel,
  useArchiveFlowModel,
  useFlowModels,
} from './api';
import type { FlowModel } from './types';

/**
 * Flow Models list. Each row expands to show its step grid; the pencil
 * icon opens the full editor (live SUM=100% validator, add/remove rows,
 * strict-increasing nb_days check) at /parameters/flow-models/:id.
 */
export function FlowModelsPage() {
  const list = useFlowModels();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Flow Models</h1>
          <p className="text-sm text-muted-foreground">
            Named amortization profiles. Total of step <code>value_pct</code> must equal 100 %.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm">
            <Link to="/parameters/flow-models/new">
              <Plus className="mr-2 h-4 w-4" />
              New flow model
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            {list.isFetching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Reload
          </Button>
        </div>
      </header>

      {list.isError ? <ApiErrorBanner error={list.error} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All flow models</CardTitle>
          <CardDescription>
            {list.data ? `${list.data.length} model(s)` : 'Loading…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (list.data ?? []).length === 0 ? (
            <p className="px-4 py-6 text-center text-muted-foreground">
              No flow models yet.
            </p>
          ) : (
            <ul className="divide-y">
              {list.data!.map((fm) => (
                <FlowModelRow
                  key={fm.id}
                  fm={fm}
                  expanded={expanded.has(fm.id)}
                  onToggle={() => toggle(fm.id)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FlowModelRow({
  fm,
  expanded,
  onToggle,
}: {
  fm: FlowModel;
  expanded: boolean;
  onToggle: () => void;
}) {
  const approve = useApproveFlowModel(fm.id);
  const archive = useArchiveFlowModel(fm.id);
  const isAdmin = useAuthStore((x) => x.hasRole('ALM_ADMIN'));

  const sumPct = typeof fm.sumValuePct === 'number' ? fm.sumValuePct : null;
  const sumOk = sumPct !== null && Math.abs(sumPct - 1) < 0.0001;

  return (
    <li>
      <div className="flex items-center gap-3 px-4 py-3">
        <Button variant="ghost" size="sm" onClick={onToggle}>
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{fm.code}</span>
            <span className="font-medium">{fm.name}</span>
            <StatusBadge status={fm.status} />
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {fm.totalPeriods ?? 0} periods · horizon {fm.horizonDays ?? '?'} d
            {sumPct !== null ? (
              <>
                {' · '}
                <span className={sumOk ? 'text-emerald-700' : 'text-destructive'}>
                  Σ value_pct = {(sumPct * 100).toFixed(4)}%
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {fm.status !== 'ARCHIVED' ? (
            <Button asChild variant="ghost" size="sm" title="Edit steps">
              <Link to={`/parameters/flow-models/${fm.id}`}>
                <Pencil className="h-4 w-4" />
              </Link>
            </Button>
          ) : null}
          {isAdmin && fm.status !== 'ACTIVE' && fm.status !== 'ARCHIVED' ? (
            <Button
              variant="ghost"
              size="sm"
              title="Approve"
              disabled={approve.isPending}
              onClick={() => approve.mutate()}
            >
              <ShieldCheck className="h-4 w-4 text-primary" />
            </Button>
          ) : null}
          {isAdmin && fm.status !== 'ARCHIVED' ? (
            <Button
              variant="ghost"
              size="sm"
              title="Archive"
              disabled={archive.isPending}
              onClick={() => archive.mutate()}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          ) : null}
        </div>
      </div>

      {expanded ? (
        <div className="border-t bg-muted/20 px-4 py-3">
          {fm.description ? (
            <p className="mb-2 text-xs text-muted-foreground">{fm.description}</p>
          ) : null}
          <div className="overflow-x-auto rounded border bg-background">
            <table className="w-full text-xs">
              <thead className="border-b bg-muted/40 uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium">#</th>
                  <th className="px-3 py-1.5 text-left font-medium">Period</th>
                  <th className="px-3 py-1.5 text-right font-medium">Days</th>
                  <th className="px-3 py-1.5 text-right font-medium">Value %</th>
                </tr>
              </thead>
              <tbody>
                {fm.steps.map((s) => (
                  <tr key={s.stepIndex} className="border-b last:border-0">
                    <td className="px-3 py-1 text-muted-foreground">{s.stepIndex}</td>
                    <td className="px-3 py-1">{s.periodLabel}</td>
                    <td className="px-3 py-1 text-right tabular-nums text-muted-foreground">
                      {s.nbDays}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-1 text-right tabular-nums',
                        s.valuePct < 0 ? 'text-destructive' : '',
                      )}
                    >
                      {(s.valuePct * 100).toFixed(4)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {(approve.isError || archive.isError) && (
        <div className="px-4 pb-3">
          {approve.isError ? <ApiErrorBanner error={approve.error} /> : null}
          {archive.isError ? <ApiErrorBanner error={archive.error} /> : null}
        </div>
      )}
    </li>
  );
}
